import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api, { BACKEND_URL } from '@/lib/api';
import { Upload, Trash2, Copy, Image, AlertTriangle, CheckCircle, ShieldAlert, Filter } from 'lucide-react';

export default function AdminMedia() {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, media: null, loading: false, error: null });
  const [filterUnused, setFilterUnused] = useState(false);

  const load = useCallback(() => {
    api.get('/media').then(r => setMedia(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        await api.post('/media/upload', fd);
      }
      toast.success('Upload erfolgreich');
      load();
    } catch { toast.error('Upload fehlgeschlagen'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const openDeleteDialog = (mediaItem) => {
    setDeleteDialog({ open: true, media: mediaItem, loading: false, error: null });
  };

  const handleDelete = async () => {
    if (!deleteDialog.media) return;
    setDeleteDialog(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      await api.delete(`/media/${deleteDialog.media.id}`);
      toast.success('Bild gelöscht');
      setDeleteDialog({ open: false, media: null, loading: false, error: null });
      load();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Fehler beim Löschen';
      setDeleteDialog(prev => ({ ...prev, loading: false, error: errorMsg }));
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL kopiert');
  };

  // Filter media based on usage
  const filteredMedia = filterUnused ? media.filter(m => !m.is_used) : media;
  
  // Count stats
  const usedCount = media.filter(m => m.is_used).length;
  const unusedCount = media.filter(m => !m.is_used).length;

  return (
    <div data-testid="admin-media">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0E518D]">Medien</h1>
          <p className="text-sm text-slate-500 mt-1">
            {media.length} Bilder • <span className="text-green-600">{usedCount} in Verwendung</span> • <span className="text-orange-500">{unusedCount} ungenutzt</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={filterUnused ? "default" : "outline"}
            onClick={() => setFilterUnused(!filterUnused)}
            className={`rounded-sm text-xs ${filterUnused ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
            data-testid="filter-unused-btn"
          >
            <Filter className="h-3.5 w-3.5 mr-1" />
            {filterUnused ? 'Alle anzeigen' : 'Nur ungenutzte'}
          </Button>
          <label className="cursor-pointer">
            <input type="file" multiple accept="image/*" onChange={handleUpload} className="hidden" disabled={uploading} data-testid="media-upload-input" />
            <Button asChild className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide text-xs" disabled={uploading}>
              <span><Upload className="h-4 w-4 mr-1" /> {uploading ? 'Hochladen...' : 'Hochladen'}</span>
            </Button>
          </label>
        </div>
      </div>

      {filteredMedia.length === 0 ? (
        <div className="text-center py-16">
          <Image className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-400">
            {filterUnused ? 'Keine ungenutzten Bilder vorhanden' : 'Noch keine Medien hochgeladen'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredMedia.map(m => {
            const fullUrl = m.url ? (m.url.startsWith('/api') ? `${BACKEND_URL}${m.url}` : m.url) : '';
            return (
              <Card key={m.id} className={`bg-white rounded-sm shadow-sm border overflow-hidden group ${m.is_used ? 'border-green-200' : 'border-orange-200'}`} data-testid={`media-item-${m.id}`}>
                <div className="aspect-square bg-slate-50 relative">
                  <img src={fullUrl} alt={m.original_name} className="w-full h-full object-cover" />
                  
                  {/* Usage Badge */}
                  <div className="absolute top-2 left-2">
                    {m.is_used ? (
                      <Badge className="bg-green-500/90 text-white text-[10px] px-1.5 py-0.5 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" /> In Verwendung
                      </Badge>
                    ) : (
                      <Badge className="bg-orange-500/90 text-white text-[10px] px-1.5 py-0.5 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> Ungenutzt
                      </Badge>
                    )}
                  </div>
                  
                  {/* Hover Actions */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button onClick={() => copyUrl(m.url)} className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-slate-100" data-testid={`copy-url-${m.id}`} title="URL kopieren">
                      <Copy className="h-3.5 w-3.5 text-[#0E518D]" />
                    </button>
                    <button onClick={() => openDeleteDialog(m)} className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-red-50" data-testid={`delete-media-${m.id}`} title="Löschen">
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </button>
                  </div>
                </div>
                <CardContent className="p-2">
                  <p className="text-xs text-slate-500 truncate" title={m.original_name}>{m.original_name}</p>
                  {m.used_in && m.used_in.length > 0 && (
                    <p className="text-[10px] text-green-600 mt-0.5">{m.used_in.join(', ')}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !deleteDialog.loading && setDeleteDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {deleteDialog.media?.is_used ? (
                <ShieldAlert className="h-5 w-5 text-red-500" />
              ) : (
                <Trash2 className="h-5 w-5 text-orange-500" />
              )}
              Bild löschen
            </DialogTitle>
            <DialogDescription>
              {deleteDialog.media?.is_used ? (
                <span className="text-red-600">
                  Dieses Bild wird noch verwendet und kann nicht gelöscht werden.
                </span>
              ) : (
                <>
                  Möchten Sie dieses Bild wirklich löschen?
                  <br />
                  <span className="text-slate-500">Dateiname: {deleteDialog.media?.original_name}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Preview */}
          {deleteDialog.media && (
            <div className="flex justify-center my-4">
              <img 
                src={deleteDialog.media.url.startsWith('/api') ? `${BACKEND_URL}${deleteDialog.media.url}` : deleteDialog.media.url} 
                alt={deleteDialog.media.original_name}
                className="max-h-40 max-w-full rounded-sm border border-slate-200"
              />
            </div>
          )}

          {/* Usage Info */}
          {deleteDialog.media?.is_used && deleteDialog.media?.used_in && (
            <div className="bg-red-50 border border-red-200 rounded-sm p-3 mb-4">
              <p className="text-sm text-red-700 font-medium">Verwendung:</p>
              <ul className="text-sm text-red-600 mt-1">
                {deleteDialog.media.used_in.map((use, i) => (
                  <li key={i}>• {use}</li>
                ))}
              </ul>
              <p className="text-xs text-red-500 mt-2">Bitte entfernen Sie das Bild zuerst aus den genannten Bereichen.</p>
            </div>
          )}

          {/* Error Message */}
          {deleteDialog.error && (
            <div className="bg-red-50 border border-red-200 rounded-sm p-3 mb-4">
              <p className="text-sm text-red-600">{deleteDialog.error}</p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, media: null, loading: false, error: null })}
              disabled={deleteDialog.loading}
              className="rounded-sm"
            >
              Abbrechen
            </Button>
            {!deleteDialog.media?.is_used && (
              <Button 
                onClick={handleDelete} 
                disabled={deleteDialog.loading}
                className="bg-red-500 hover:bg-red-600 text-white rounded-sm"
                data-testid="confirm-delete-btn"
              >
                {deleteDialog.loading ? 'Lösche...' : 'Endgültig löschen'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
